import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, MapPin, Calendar, Archive } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { SectionMark } from "@/components/PageHeader";
import ShareEventButton from "./ShareEventButton";
import { splitEvents, fmtEventDates, pick } from "./eventUtils";
import EventCountdown from "./EventCountdown";

export function useEvents() {
    const [events, setEvents] = useState(null);
    useEffect(() => {
        api.get("/events").then((r) => setEvents(r.data || [])).catch(() => setEvents([]));
    }, []);
    return events;
}

function FeaturedEvent({ ev, lang }) {
    const en = lang === "en";
    const to = `/eventi/${ev.slug}`;
    return (
        <div data-testid="home-featured-event" className="grid md:grid-cols-12 gap-6 md:gap-10 items-center border border-abnormal-acid/50 bg-abnormal-charcoal p-4 md:p-6">
            <Link to={to} className="md:col-span-5 lg:col-span-4 block bg-black border border-abnormal-paper/30 max-w-sm md:max-w-none mx-auto" aria-label={pick(ev, "title", lang)}>
                {ev.poster && <img src={ev.poster} alt={pick(ev, "title", lang)} loading="eager" decoding="async" className="w-full h-auto block" />}
            </Link>
            <div className="md:col-span-7 lg:col-span-8">
                <p className="label-mono text-abnormal-acid flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-abnormal-acid animate-pulse" /> FEATURED EVENT</span>
                    {ev.series && <><span className="text-abnormal-paperDim/50">·</span><span className="text-abnormal-paperDim">{ev.series}</span></>}
                </p>
                <h3 data-testid="home-featured-title" className="mt-3 font-display uppercase leading-[0.85] tracking-tighter text-abnormal-paper text-4xl sm:text-5xl lg:text-6xl">
                    {pick(ev, "title", lang)}<span className="text-abnormal-acid">.</span>
                </h3>
                <p className="mt-4 flex flex-wrap gap-x-5 gap-y-1.5 font-display uppercase text-abnormal-paper text-lg md:text-2xl leading-none tracking-tight">
                    <span className="inline-flex items-center gap-2"><Calendar size={16} className="text-abnormal-acid" /> {fmtEventDates(ev, lang)}</span>
                    <span className="inline-flex items-center gap-2 text-abnormal-paperDim"><MapPin size={16} className="text-abnormal-acid" /> {ev.location}</span>
                    <EventCountdown ev={ev} lang={lang} compact testid="home-featured-days" />
                </p>
                {pick(ev, "highlight", lang) && (
                    <p data-testid="home-featured-highlight" className="mt-5 font-display uppercase leading-none tracking-tight text-abnormal-magenta text-2xl md:text-3xl misprint">
                        {pick(ev, "highlight", lang)}
                    </p>
                )}
                <p className="mt-3 text-abnormal-paperDim text-sm md:text-base leading-relaxed max-w-xl">{pick(ev, "summary", lang)}</p>
                <div className="mt-6 flex flex-wrap items-center gap-3">
                    <Link to={to} data-testid="home-featured-view" className="btn-primary">VIEW EVENT <ArrowRight size={14} /></Link>
                    {ev.registration_url && (
                        <a href={ev.registration_url} target="_blank" rel="noreferrer" data-testid="home-featured-book"
                            className="inline-flex items-center gap-2 bg-abnormal-magenta text-black hover:bg-abnormal-paper px-6 py-3 text-xs uppercase tracking-[0.2em] font-bold transition-colors">
                            BOOK NOW <ArrowUpRight size={13} />
                        </a>
                    )}
                    <ShareEventButton event={ev} />
                </div>
            </div>
        </div>
    );
}

function NextUp({ ev, lang }) {
    const en = lang === "en";
    const to = `/eventi/${ev.slug}`;
    return (
        <article data-testid={`home-next-event-${ev.slug}`} className="group flex gap-4 border border-white/15 hover:border-abnormal-acid bg-abnormal-charcoal p-3 md:p-4 transition-colors">
            <Link to={to} className="w-24 sm:w-32 flex-shrink-0 bg-black aspect-[4/5] border border-white/10" aria-label={pick(ev, "title", lang)}>
                {ev.poster && <img src={ev.poster} alt={pick(ev, "title", lang)} loading="lazy" className="w-full h-full object-contain" />}
            </Link>
            <div className="min-w-0 flex-1 flex flex-col">
                <p className="label-mono text-abnormal-acid">{en ? "NEXT UP" : "A SEGUIRE"}{ev.series ? ` · ${ev.series}` : ""}</p>
                <h4 className="mt-1 font-display uppercase leading-[0.9] tracking-tight text-abnormal-paper text-xl sm:text-2xl md:text-3xl">
                    <Link to={to} className="hover:text-abnormal-acid transition-colors">{pick(ev, "title", lang)}</Link>
                </h4>
                <p className="mt-2 label-mono text-abnormal-paperDim">{fmtEventDates(ev, lang)} · {ev.location}</p>
                <p className="mt-2 text-sm text-abnormal-paperDim leading-snug line-clamp-2">{pick(ev, "highlight", lang) || pick(ev, "summary", lang)}</p>
                <div className="mt-auto pt-3 flex flex-wrap gap-2">
                    <Link to={to} data-testid={`home-next-discover-${ev.slug}`} className="inline-flex items-center gap-1.5 border border-abnormal-paper/40 hover:border-abnormal-acid hover:text-abnormal-acid px-3 py-2 text-[10px] uppercase tracking-[0.2em] font-bold transition-colors">
                        DISCOVER <ArrowRight size={11} />
                    </Link>
                    {ev.registration_url && (
                        <a href={ev.registration_url} target="_blank" rel="noreferrer" data-testid={`home-next-book-${ev.slug}`}
                            className="inline-flex items-center gap-1.5 bg-abnormal-acid text-black hover:bg-abnormal-paper px-3 py-2 text-[10px] uppercase tracking-[0.2em] font-bold transition-colors">
                            BOOK NOW <ArrowUpRight size={11} />
                        </a>
                    )}
                </div>
            </div>
        </article>
    );
}

/* Homepage block: featured upcoming event + what's next + archive link. Fully date-driven (Europe/Rome). */
export default function NextEvents({ lang }) {
    const en = lang === "en";
    const events = useEvents();
    if (events === null) return null;
    const { upcoming, past, featured } = splitEvents(events);
    const rest = upcoming.slice(1, 3);
    return (
        <section data-testid="home-next-events" className="relative bg-black border-y border-white/10">
            <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-12 md:py-16">
                <SectionMark n="02" title="NEXT EVENTS"
                    right={<Link to="/eventi" data-testid="home-events-all" className="label-mono text-abnormal-acid hover:underline underline-offset-4 inline-flex items-center gap-1">{en ? "All events" : "Tutti gli eventi"} <ArrowRight size={12} /></Link>} />
                {featured ? (
                    <div className="grid lg:grid-cols-12 gap-6">
                        <div className={rest.length ? "lg:col-span-8" : "lg:col-span-12"}><FeaturedEvent ev={featured} lang={lang} /></div>
                        {rest.length > 0 && (
                            <div className="lg:col-span-4 flex flex-col gap-4">
                                {rest.map((ev) => <NextUp key={ev.slug} ev={ev} lang={lang} />)}
                            </div>
                        )}
                    </div>
                ) : (
                    <div data-testid="home-no-events" className="border border-white/15 bg-abnormal-charcoal p-8 md:p-12 halftone">
                        <p className="font-display uppercase leading-[0.9] tracking-tight text-abnormal-paper text-3xl sm:text-5xl md:text-6xl">
                            The next ride is coming.<br /><span className="text-abnormal-acid">Stay Abnormal.</span>
                        </p>
                    </div>
                )}
                {past.length > 0 && (
                    <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
                        <Link to="/eventi#past" data-testid="home-explore-past" className="btn-ghost">
                            <Archive size={14} /> EXPLORE PAST EVENTS <ArrowRight size={13} />
                        </Link>
                        <p className="label-mono text-abnormal-paperDim/60">{past.length} {en ? (past.length === 1 ? "event in the archive" : "events in the archive") : (past.length === 1 ? "evento in archivio" : "eventi in archivio")}</p>
                    </div>
                )}
            </div>
        </section>
    );
}
